<script setup lang="ts">
import { computed, ref, useI18n } from '#imports';
import { LanguageSelector, Toast } from 'r3ui-lib';

const { locale, locales, setLocale, loadLocaleMessages, te, t } = useI18n();
const errorMessage = ref('');
const isSwitching = ref(false);
const localeOptions = computed<Array<{ id: string; label: string }>>(() =>
  locales.value.map((item: { code: string; name?: string }) => ({
    id: item.code,
    label: item.name ?? item.code,
  }))
);

async function setLocaleHandler(value: string) {
  if (isSwitching.value || value === locale.value) return;
  if (!localeOptions.value.some((item: { id: string }) => item.id === value)) {
    throw new TypeError('Unsupported locale');
  }
  isSwitching.value = true;
  errorMessage.value = '';
  try {
    const nextLocale = value as typeof locale.value;
    await loadLocaleMessages(nextLocale);
    // Nuxt I18n перехватывает ошибки загрузчика: проверяем словарь до смены языка.
    if (!te('actions', nextLocale) || !te('actions', 'en')) {
      throw new Error('Locale messages were not loaded');
    }
    await setLocale(nextLocale);
  } catch {
    errorMessage.value = t('pages.errors.unexpected');
  } finally {
    isSwitching.value = false;
  }
}
</script>

<template>
  <div :inert="isSwitching" :aria-busy="isSwitching">
    <LanguageSelector
      :model-value="locale"
      :options="localeOptions"
      @update:model-value="setLocaleHandler"
    />
  </div>
  <Toast
    :message="errorMessage"
    :is-error="true"
    :as-html="false"
    :close-label="t('close')"
    @clear-message="errorMessage = ''"
  />
</template>
